/* MealHistoryTrends — DIL/NIS/satiety trends по месеци (iter420.2).
 *
 * Използва recharts LineChart с 3 серии. Auto-fits responsive.
 * Empty state когато няма достатъчно данни (< 2 buckets).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid } from "recharts";
import { Loader2, TrendingUp, Calendar } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/App";

export default function MealHistoryTrends({ isDark, bg }) {
  const { API } = useAuth();
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState({ series: [] });
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(`${API}/health-intelligence/history?period=${period}&months=6`);
      setData(resp);
    } catch {
      setData({ series: [] });
    } finally {
      setLoading(false);
    }
  }, [API, period]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const series = data.series || [];
  const hasData = series.length >= 1;

  return (
    <Card className={`border ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}
          data-testid="hi-meal-history">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <TrendingUp className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "История на храненето" : "Meal history"}
            </h3>
          </div>
          <div className="flex gap-1">
            {[
              ["weekly", bg ? "Седмично" : "Weekly"],
              ["monthly", bg ? "Месечно" : "Monthly"],
            ].map(([id, label]) => (
              <button key={id}
                      onClick={() => setPeriod(id)}
                      data-testid={`hi-history-period-${id}`}
                      className={`text-[10.5px] px-2.5 py-1 rounded-full font-bold transition-colors ${
                        period === id
                          ? (isDark ? "bg-emerald-500/30 text-emerald-200" : "bg-emerald-600 text-white")
                          : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                      }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className={`w-6 h-6 animate-spin ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
          </div>
        ) : !hasData ? (
          <div className={`h-64 flex flex-col items-center justify-center text-center ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            <Calendar className="w-8 h-8 mb-2 opacity-40" />
            <p className="text-xs">
              {bg
                ? "Запази повече хранения от Plate Simulator за да видиш trends"
                : "Save more meals from Plate Simulator to see trends"}
            </p>
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                <CartesianGrid stroke={isDark ? "#1E293B" : "#E2E8F0"} strokeDasharray="3 3" />
                <XAxis dataKey="bucket" stroke={isDark ? "#64748B" : "#94A3B8"} fontSize={10} />
                <YAxis stroke={isDark ? "#64748B" : "#94A3B8"} fontSize={10} />
                <Tooltip
                  contentStyle={{
                    background: isDark ? "#0F172A" : "#FFFFFF",
                    border: `1px solid ${isDark ? "#1E293B" : "#E2E8F0"}`,
                    borderRadius: 8, fontSize: 11,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="dil_avg"
                      stroke="#F59E0B" strokeWidth={2}
                      name={bg ? "DIL (g)" : "DIL (g)"}
                      dot={{ r: 3, fill: "#F59E0B" }} />
                <Line type="monotone" dataKey="nis_avg_pct"
                      stroke="#8B5CF6" strokeWidth={2}
                      name={bg ? "NIS (%)" : "NIS (%)"}
                      dot={{ r: 3, fill: "#8B5CF6" }} />
                <Line type="monotone" dataKey="satiety_avg"
                      stroke="#10B981" strokeWidth={2}
                      name={bg ? "Ситост" : "Satiety"}
                      dot={{ r: 3, fill: "#10B981" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
